import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { getLocalDateString } from '@shared/utils/dateUtils';

/**
 * Detects local day rollover via visibilitychange (web), appStateChange (native), + 60s interval.
 * Returns the current local date string (YYYY-MM-DD), updating on day change.
 */
export function useDayChange(): string {
    const [localDate, setLocalDate] = useState(getLocalDateString);

    const checkDate = useCallback(() => {
        setLocalDate(prev => {
            const now = getLocalDateString();
            return now === prev ? prev : now;
        });
    }, []);

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkDate();
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        const interval = setInterval(checkDate, 60_000);

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            clearInterval(interval);
        };
    }, [checkDate]);

    // Native iOS/Android: detect foreground resume via Capacitor App listener
    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return;
        const listener = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
            if (isActive) {
                checkDate();
            }
        });
        return () => { listener.then(h => h.remove()); };
    }, [checkDate]);

    return localDate;
}
